import { motion } from "framer-motion";
import { Eye, HeartHandshake, Workflow } from "lucide-react";

const pillars = [
  {
    icon: Eye,
    letter: "V",
    title: "Visibility",
    text: "Patients can't choose you if they can't find you. We make your clinic show up where patients actually look: Google Search, Google Maps, and AI answers.",
    points: ["Local SEO & Google Business Profile", "Google Maps rankings", "AI search optimization"],
  },
  {
    icon: HeartHandshake,
    letter: "T",
    title: "Trust",
    text: "Patients choose the clinic they trust. We build that trust before the first phone call with reviews, real content, and a professional online presence.",
    points: ["Review generation systems", "Real photo & video content", "Doctor authority building"],
  },
  {
    icon: Workflow,
    letter: "S",
    title: "Systems",
    text: "Attention without conversion is wasted money. We tighten the path from first click to scheduled appointment so interest turns into booked patients.",
    points: ["Website conversion", "Patient journey & follow-up", "Front desk support"],
  },
];

const VTSCards = () => {
  return (
    <section id="vts" className="section-padding section-navy">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-primary font-semibold tracking-[0.2em] uppercase text-sm mb-4">
            The Framework
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4 text-white">
            Visibility. Trust. <span className="gold-gradient-text">Systems.</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            Every dental clinic that grows consistently has all three working together. Most are missing at least one.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="card-glass p-8 relative overflow-hidden"
            >
              <span className="absolute -top-4 -right-2 font-heading text-[7rem] leading-none text-primary/10 select-none">
                {p.letter}
              </span>
              <p.icon className="w-10 h-10 text-primary mb-5 relative" />
              <h3 className="font-heading text-2xl mb-3 text-white relative">{p.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6 relative">{p.text}</p>
              <ul className="space-y-2 relative">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-3 text-sm text-white/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VTSCards;
